import { Koma, ChuSmallGrid } from './ChuRuleDiagrams';
import { ChuLionDetails } from './ChuLionDetails';
import './RulePaperSheets.css';

const INK = '#252725';
const PAPER = '#fffefa';

function GoTurns() {
  return <svg viewBox="0 0 308 110" role="img" aria-label="黒が1個置き、白が1個置き、次に黒が1個置きます。丸い印はその番で置いた石です。">
    {[0,1,2].map(step=><g key={step} transform={`translate(${step*106+10} 0)`}>
      {[0,1,2].map(i=><g key={i} stroke="#c5c1b4"><path d={`M5 ${15+i*28.5}H80`} /><path d={`M${12+i*28.5} 8V80`} /></g>)}
      <circle cx="40.5" cy="43.5" r="8.5" fill={INK} />
      {step>0&&<circle cx="69" cy="43.5" r="8.5" fill={PAPER} stroke={INK} />}
      {step>1&&<circle cx="40.5" cy="72" r="8.5" fill={INK} />}
      <circle cx={step===1?69:40.5} cy={step===2?72:43.5} r="11.5" fill="none" stroke="#7e8b73" />
      <text x="41" y="104" textAnchor="middle" fontSize="13">{step===1?'白の番':'黒の番'}</text>
    </g>)}
  </svg>;
}

function GoGroupCapture() {
  return <svg viewBox="0 0 308 139" role="img" aria-label="縦横につながった相手の白の石2個を、上下左右から黒で囲みます。最後の空きへ黒を置くと、白の石全体が盤からなくなります。">
    {[0,1].map(after=><g key={after} transform={`translate(${after*154} 0)`}>
      {[0,1,2].map(i=><path key={`h${i}`} d={`M7 ${34+i*29}H128`} stroke="#bebbac" />)}
      {[0,1,2,3].map(i=><path key={`v${i}`} d={`M${22+i*29} 18V108`} stroke="#bebbac" />)}
      {[[22,63],[51,34],[80,34],[51,92],[80,92],...(after?[[109,63]]:[])].map(([x,y])=><circle key={`${x},${y}`} cx={x} cy={y} r="11" fill={INK} />)}
      {!after&&<><circle cx="51" cy="63" r="11" fill={PAPER} stroke={INK} /><circle cx="80" cy="63" r="11" fill={PAPER} stroke={INK} /><circle cx="109" cy="63" r="13" fill="none" stroke="#7e8b73" strokeDasharray="3 3" /><path d="M109 24V47" stroke="#7e8b73" /><text x="105" y="14" textAnchor="middle" fontSize="13">黒を置く</text></>}
      {Boolean(after)&&<circle cx="109" cy="63" r="13" fill="none" stroke="#7e8b73" />}
      <text x="68" y="134" textAnchor="middle" fontSize="13">{after?'白の石2個を取ります':'置く前'}</text>
    </g>)}
  </svg>;
}

// A 98-ply legal variation of a real CPU game; both chains have two genuine eyes. See docs/qa/cheatsheet-three-complete-20261010/ending.json.
const GO_ENDING_ROWS = ["WWWWWBBBB", "WWWWWBB.B", "WWWWWWBBB", "WWWWBBBBB", "WWWWBB.BB", ".WWBBBBBB", "WWWBBBBBB", ".WWWBBBBB", "WWWWBBBBB"] as const;

function GoFinalCount() {
  return <svg viewBox="0 0 308 190" role="img" aria-label="黒41個、白36個が残った盤です。白には6.5点を足すので42.5点となり、白の勝ちです。">
    <rect x="0" y="8" width="176" height="176" rx="1" fill="#f3eddd" />
    {[0,1,2,3,4,5,6,7,8].map(i=><g key={i} stroke="#bdb9aa" strokeWidth=".8"><path d={`M10 ${18+i*19.5}H166`} /><path d={`M${10+i*19.5} 18V174`} /></g>)}
    {GO_ENDING_ROWS.flatMap((row,r)=>[...row].map((stone,c)=>stone==='.'?null:<circle key={`${r},${c}`} cx={10+c*19.5} cy={18+r*19.5} r="8.2" fill={stone==='B'?INK:PAPER} stroke={stone==='W'?INK:'none'} strokeWidth=".8" />))}
    <circle cx="195" cy="37" r="6" fill={INK} /><text x="209" y="43" fontSize="16">黒 41点</text>
    <circle cx="195" cy="72" r="6" fill={PAPER} stroke={INK} /><text x="209" y="78" fontSize="16">白 36点</text>
    <text x="190" y="104" fontSize="14">白には6.5点を</text>
    <text x="190" y="125" fontSize="14">足します。</text>
    <text x="190" y="158" fontSize="20">41 ＜ 42.5</text>
    <text x="190" y="185" fontSize="14">白の勝ちです。</text>
  </svg>;
}

export function JungoRulePaper() {
  return <article className="rule-paper rule-paper--go go-picture-paper">
    <section className="go-turns"><h2>交点に交互に石を1個ずつ置きます。</h2><figure><GoTurns /></figure></section>
    <section className="go-capture"><h2>縦横につながった相手の石を上下左右から自分の石で囲むと全部取れます。</h2><figure><GoGroupCapture /></figure></section>
    <section className="go-count"><h2>最後は残った石の数を比べます。</h2><figure><GoFinalCount /></figure></section>
    <p className="go-ending rule-small">二人が続けてパスすると終わります。</p>
  </article>;
}

function BridgeCard({x,y,rank,suit,width=32,height=42,muted=false,strong=false}: {x:number;y:number;rank:string;suit:string;width?:number;height?:number;muted?:boolean;strong?:boolean}) {
  const red=suit==='♥'||suit==='♦';
  return <g transform={`translate(${x} ${y})`} opacity={muted ? 0.28 : 1}>
    <rect width={width} height={height} rx="3" fill={PAPER} stroke={strong?INK:'#aaa99f'} strokeWidth={strong?2:1} />
    <text x={width*.2} y={height*.44} fontFamily="Georgia, serif" fontSize={height*.39} fill={red?'#b42331':INK}>{rank}</text>
    <text x={width*.55} y={height*.83} fontFamily="Georgia, serif" fontSize={height*.43} fill={red?'#b42331':INK}>{suit}</text>
  </g>;
}


function BridgeChoice() {
  return <svg viewBox="0 0 308 112" role="img" aria-label="最初にスペード7が出ました。スペードK、スペード2、ハート9があればスペードから選びます。ハートQ、ダイヤA、クラブ9のほかにも、スペードのない手札なら、どれでも出せます。図は手札の一部です。">
    <text x="154" y="15" fontSize="14" textAnchor="middle">最初の札：♠7</text>

    <BridgeCard x={15} y={28} rank="K" suit="♠" strong /><BridgeCard x={54} y={28} rank="2" suit="♠" strong /><BridgeCard x={93} y={28} rank="9" suit="♥" muted />
    <BridgeCard x={171} y={28} rank="Q" suit="♥" strong /><BridgeCard x={210} y={28} rank="A" suit="♦" strong /><BridgeCard x={249} y={28} rank="9" suit="♣" strong />
    <path d="M98 32l23 32M121 32L98 64" stroke="#9f9b8d" strokeWidth="1" />
    <text x="230" y="88" fontSize="14" textAnchor="middle">♠がなければ、<tspan x="230" dy="21">どれでも出せます。</tspan></text>
  </svg>;
}

function TrickCards() {
 return <svg viewBox="0 0 308 164" role="img" aria-label="上下の2手札が自分、左右が相手です。上、右、下、左の時計回りに出します。最初のスペードで一番強い下のAが1勝し、次は下の手札から始めます。">
 <g stroke="#9ba18f" strokeWidth="1.2" fill="none">
 <path d="M136 92A25 25 0 1 1 176 87m0 -8l0 8 7 -3" />
 </g>
 {([['7','♠',135,0,121,18],['K','♠',248,51,291,70],['A','♠',135,100,121,120],['Q','♥',23,51,8,70]] as const).map(([rank,suit,x,y,nx,ny],i)=><g key={i}>
 <text x={nx} y={ny} textAnchor="middle" fontSize="14">{i+1}</text><BridgeCard x={x} y={y} rank={rank} suit={suit} width={36} height={42} strong={i===2} />
 </g>)}
 <text x="179" y="22" fontSize="13">自分</text><text x="153" y="161" textAnchor="middle" fontSize="13">自分</text><text x="41" y="108" textAnchor="middle" fontSize="13">相手</text><text x="266" y="108" textAnchor="middle" fontSize="13">相手</text>
 <text x="181" y="125" fontSize="14">1勝します</text><text x="181" y="146" fontSize="13">次はここから</text><path d="M178 141L173 136m0 5v-5h5" fill="none" stroke="#73766e" />
 </svg>;
}

function BridgeIdeal() {
  return <svg viewBox="0 0 308 106" role="img" aria-label="自分の勝ち数を理想と比べる例です。実際8勝、理想7勝なら自分の勝ちです。実際6勝、理想7勝なら相手の勝ちです。同じ7勝なら引き分けです。">
    <text x="6" y="14" fontSize="14">実際</text><text x="84" y="14" fontSize="14">理想</text><text x="153" y="14" fontSize="14">自分の勝ち数</text>
    {([['8','＞','自分の勝ちです。'],['6','＜','相手の勝ちです。'],['7','＝','引き分けです。']] as const).map(([actual,sign,result],i)=><g key={actual} transform={`translate(0 ${i*29})`}>
      <text x="6" y="42" fontSize="21">{actual}勝</text><text x="49" y="42" fontSize="18">{sign}</text><text x="84" y="42" fontSize="21">7勝</text><text x="143" y="42" fontSize="14">{result}</text>
    </g>)}
  </svg>;
}

export function BridgeRulePaper() {
  return <article className="rule-paper rule-paper--bridge bridge-picture-paper">
    <p className="rule-intro">一人が13枚ずつの手札を2つ使います。</p>
    <section className="bridge-choice"><h2>最初のマークと同じマークを出します。</h2><figure><BridgeChoice /></figure></section>
    <section className="bridge-taking"><h2>最初のマークで最も強い札が1勝します。</h2><figure><TrickCards /><figcaption>次は勝った人から、時計回りに出します。</figcaption></figure><p className="rule-small bridge-ranks">強い順にA、K、Q、J、10……2です。</p></section>
    <section className="bridge-ideal"><h2>13回で終わり、自分の勝ち数を理想と比べます。</h2><p className="rule-small">すべての札が見え、二人とも最善を選んだときの勝ち数が「理想」です。</p><figure><BridgeIdeal /></figure></section>
  </article>;
}

function TakenKoma({x,y,label}:{x:number;y:number;label:string}) {
  return <g><g opacity=".3"><Koma x={x} y={y} label={label} /></g><path d={`M${x-12} ${y-13}l24 26M${x+12} ${y-13}l-24 26`} stroke="#a56a5d" strokeWidth="1.5" /></g>;
}

function RoyalStory() {
  return <svg viewBox="0 0 308 142" role="img" aria-label="王だけなら、王を取ると勝ちです。太子もいる場合は、王を取っても続きます。王と太子を両方取ると勝ちです。酔象が成ると太子になります。">
    <text x="43" y="14" fontSize="14" textAnchor="middle">王だけ</text><text x="153" y="14" fontSize="14" textAnchor="middle">太子もいます</text><text x="264" y="14" fontSize="14" textAnchor="middle">両方取る</text>
    <TakenKoma x={43} y={38} label="王" /><TakenKoma x={136} y={38} label="王" /><Koma x={173} y={38} label="太" /><TakenKoma x={246} y={38} label="王" /><TakenKoma x={283} y={38} label="太" />
    <text x="43" y="72" fontSize="14" textAnchor="middle">勝ちです。</text><text x="153" y="72" fontSize="14" textAnchor="middle">続きます。</text><text x="264" y="72" fontSize="14" textAnchor="middle">勝ちです。</text>
    {[false,true].map((after,i)=><g key={i} data-promotion-frame={i} data-cell-size="24" transform={`translate(${84+i*112} 94)`}>
      <text x="12" y="-7" fontSize="13" textAnchor="middle">{after?'成った後':'成る前'}</text>
      <rect width="24" height="24" fill="none" stroke="#c5c1b4" /><g data-promotion-square="0,0"><Koma x={12} y={12} label={after?'太':'酔'} /></g>
      <text x="12" y="44" fontSize="13" textAnchor="middle">{after?'太子':'酔象'}</text>
    </g>)}
  </svg>;
}

function LionWalking() {
  return <svg viewBox="0 0 308 94" role="img" aria-label="獅子が右上へ動いた後、向きを変えて右へ動きます。1手で2回動く例です。">
    {[0,1,2].map(step=><g key={step} transform={`translate(${step*106+10} 0)`}><ChuSmallGrid /><Koma x={14+step*24} y={step?38:62} label="獅" /><text x="38" y="90" fontSize="13" textAnchor="middle">{['動く前','1回目','2回目'][step]}</text></g>)}
  </svg>;
}

function LionCapturing() {
  return <svg viewBox="0 0 308 94" role="img" aria-label="獅子が右上へ一歩進んで相手の歩を取り、さらに右上へ進んでもう一枚取ります。二枚を一手で取ります。">
    {[0,1,2].map(step=><g key={step} transform={`translate(${step*106+10} 0)`}><ChuSmallGrid /><Koma x={14+step*24} y={62-step*24} label="獅" />{step===0&&<Koma x={38} y={38} label="歩" opponent />}{step<2&&<Koma x={62} y={14} label="歩" opponent />}<text x="38" y="90" fontSize="13" textAnchor="middle">{['動く前','1枚取る','2枚取る'][step]}</text></g>)}
  </svg>;
}

function LionJumping() {
  return <svg viewBox="0 0 308 94" role="img" aria-label="獅子が二マス先へ直接跳びます。間にある自分の歩は、跳んだ後も残っています。">
    {[0,1].map(after=><g key={after} transform={`translate(${after*154+40} 0)`}><ChuSmallGrid /><Koma x={after?62:14} y={after?14:62} label="獅" /><Koma x={38} y={38} label="歩" />{!after&&<path d="M6 54Q-8 1 47 8m-5 -5l5 5 -6 4" fill="none" stroke="#7e8b73" strokeWidth="1.5" />}<text x="38" y="90" fontSize="13" textAnchor="middle">{after?'跳んだ後':'跳ぶ前'}</text></g>)}
  </svg>;
}

export function ChuRulePaper() {
  return <article className="rule-paper rule-paper--chu chu-picture-paper">
    <section className="chu-royals"><h2>相手の王を取り、太子もいれば両方取ると勝ちです。</h2><figure><RoyalStory /></figure></section>
    <section className="chu-lion-rule"><h2>獅子は1手で2回まで動けます。</h2>
      <figure><LionWalking /><figcaption>途中で向きを変えられます。</figcaption></figure>
      <figure><LionCapturing /><figcaption>相手の駒を取った後も、もう1回動けます。</figcaption></figure>
      <figure><LionJumping /><figcaption>2マス先へは間の駒を取らずに<span className="chu-unbroken">1回で跳べます。</span></figcaption></figure>
    </section>
    <p className="chu-picture-ending rule-small">取った駒はもう使えません。</p>
    <ChuLionDetails />
  </article>;
}
